import { apiFetch } from "./client";

/**
 * The laboratory archive: ten years of findings from Bankom's own lab program, carried into the portal
 * as a record rather than as a workspace. Read only — a measurement from 2019 has no author and no time
 * in the source, so there is nobody in whose name it could be corrected here.
 */

// Kontrolne tačke su šifarnik kvaliteta i žive u svom modulu; arhiv ih samo čita. Uvoz je zato što se
// tip koristi i ovde, a izvoz zato što ekrani arhiva ne treba da znaju iz kog modula dolazi.
import type { ControlPointKind } from "./controlPoints";

export type { ControlPoint, ControlPointKind, ControlPointParameter } from "./controlPoints";

export type ParameterUsage = {
  parameterId: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  discipline: string;
  tests: number;
  measurements: number;
  /** Ordered and never measured. Across the archive this is 27.544 of 259.522. */
  neverMeasured: number;
};

export type ArchiveOverview = {
  analyses: number;
  tests: number;
  measurements: number;
  orderedNeverMeasured: number;
  firstAnalysis: string | null;
  lastAnalysis: string | null;
  withoutControlPoint: number;
  testsChosenIndividually: number;
  /** Without a point because the sampling group is missing from the lab program's codebook. */
  groupNotInCodebook: number;
  linkedToBatch: number;
  lotLabelWithoutBatch: number;
  limitsUnderReview: number;
  perYear: { year: number; analyses: number }[];
  parameters: ParameterUsage[];
};

export type AnalysisRow = {
  id: string;
  legacyId: number;
  analysedOn: string;
  controlPointId: string | null;
  controlPointCode: string | null;
  controlPointName: Record<string, string> | null;
  controlPointKind: ControlPointKind | null;
  testsChosenIndividually: boolean;
  /** The sampling group as the lab program recorded it; the only trace of a group it later deleted. */
  legacyGroupId: number | null;
  erpProductId: string | null;
  productName: string | null;
  lotText: string | null;
  goodsLotId: string | null;
  pallet: string | null;
  shiftCode: string | null;
  samplerName: string | null;
  note: string | null;
  tests: number;
  measurements: number;
};

export type LegacyLimit = {
  valueFrom: number;
  valueTo: number | null;
  codedValue: string | null;
  /** The unit is in doubt. Such a limit never judges a result — see outsideLimit. */
  needsReview: boolean;
};

export type ResultView = {
  id: string;
  ordinal: number;
  sampleLabel: string | null;
  valueNum: number | null;
  valueText: string | null;
  codedValue: string | null;
  /** null where nothing could be judged: no value, no limit, or a limit under review. Never false then. */
  outsideLimit: boolean | null;
};

export type ItemView = {
  id: string;
  parameterId: string;
  parameterKey: string;
  parameterName: Record<string, string> | null;
  unit: string;
  decimals: number;
  resultType: string | null;
  discipline: string;
  legacyTestId: number;
  legacyTestName: string | null;
  limit: LegacyLimit | null;
  results: ResultView[];
};

export type AnalysisDetail = {
  head: AnalysisRow;
  legacyRequestId: number | null;
  requestedAt: string | null;
  /** Free text from the lab program. Nearly half of it is the single letter f. Shown as found. */
  samplingPlaceLegacy: string | null;
  legacyEntityId: number | null;
  legacyEntityName: string | null;
  items: ItemView[];
};

export type MeasurementRow = {
  resultId: string;
  analysisId: string;
  analysisLegacyId: number;
  analysedOn: string;
  controlPointCode: string | null;
  controlPointName: Record<string, string> | null;
  productName: string | null;
  erpProductId: string | null;
  lotText: string | null;
  sampleLabel: string | null;
  ordinal: number;
  valueNum: number | null;
  valueText: string | null;
  codedValue: string | null;
  shiftCode: string | null;
  samplerName: string | null;
  outsideLimit: boolean | null;
};

export type ParameterSummary = {
  parameterId: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  decimals: number;
  resultType: string | null;
  discipline: string;
  measurements: number;
  orderedNeverMeasured: number;
  minValue: number | null;
  avgValue: number | null;
  maxValue: number | null;
  firstOn: string | null;
  lastOn: string | null;
  perYear: { year: number; measurements: number; minValue: number; avgValue: number; maxValue: number }[];
  limits: {
    erpProductId: string | null;
    productName: string | null;
    valueFrom: number;
    valueTo: number | null;
    codedValue: string | null;
    needsReview: boolean;
  }[];
};

/** Jedan parametar koji laboratorija meri na artiklu. */
export type ArticleParameter = {
  parameterId: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  measurements: number;
};

/**
 * Šta arhiv zna o jednoj šarži — i kad o njoj ne zna ništa, šta zna o artiklu.
 *
 * Druga polovina je poenta: samo 16.185 od 62.078 analiza nosi oznaku lota, pa je šarža bez nalaza
 * uobičajen slučaj i za proizvod koji je meren stotinama puta.
 */
export type BatchArchiveContext = {
  analyses: AnalysisRow[];
  erpProductId: string | null;
  productName: string | null;
  articleAnalyses: number;
  articleFirstOn: string | null;
  articleLastOn: string | null;
  articleParameters: ArticleParameter[];
};

/* ------------------------------------------------------------------ arhiv kao celina */

export type YearPoint = { year: number; analyses: number; measurements: number };
export type HourPoint = { hour: number; measurements: number };
/** Naručeni testovi prema onima koji nikad nisu izmereni, po godini i disciplini. */
export type UnmeasuredPoint = { year: number; discipline: string; ordered: number; unmeasured: number };
/** Za šta se koristi jedno polje oznake uzorka: HOUR, AVERAGE, ROMAN, NUMBER, OTHER. */
export type LabelKind = { kind: string; measurements: number };

/**
 * Stanje granica nasleđenih iz laboratorijskog programa.
 *
 * Deliberately not a compliance percentage: the inherited limits hang off the article and the customer,
 * not off a product specification, so a pass rate over them would measure the limits, not the goods.
 */
export type LimitHealth = {
  total: number;
  impossibleRange: number;
  zeroLowerBound: number;
  conflictingPairs: number;
  underReview: number;
  articlesWithoutLimit: number;
  articlesMeasured: number;
};

export type ParameterShape = {
  parameterId: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  discipline: string;
  measurements: number;
  minValue: number | null;
  p25: number | null;
  p50: number | null;
  p75: number | null;
  maxValue: number | null;
};

/** `name` je null za zbirni red ostalih uzorkivača, da zbir udela ostane ceo. */
export type SamplerShare = { name: string | null; analyses: number; share: number };
/** Jedna oznaka smene i koliko je različitih ljudi koristilo. */
export type ShiftCode = { code: string | null; analyses: number; samplers: number };
export type ImpossibleValues = {
  key: string;
  unit: string;
  overHundred: number;
  negative: number;
  zero: number;
  measurements: number;
};
/** `neighbours` je zbir najbliže postojeće vrednosti ispod i iznad — to je ono što od česte pravi šiljak. */
export type RepeatedValue = {
  key: string;
  value: number;
  occurrences: number;
  neighbours: number;
  parameterTotal: number;
};

export type ArchiveInsights = {
  perYear: YearPoint[];
  unmeasured: UnmeasuredPoint[];
  byHour: HourPoint[];
  labelKinds: LabelKind[];
  labelsFilled: number;
  limits: LimitHealth;
  parameters: ParameterShape[];
  samplers: SamplerShare[];
  samplersTotal: number;
  shifts: ShiftCode[];
  /** Zapisi sa pravom rotacionom oznakom (A-1 do D-2) — jedino što se ponaša kao smena. */
  shiftRotation: number;
  analysesSigned: number;
  impossible: ImpossibleValues[];
  repeated: RepeatedValue[];
};

/** Jedan dan u kretanju parametra. */
export type DayPoint = {
  day: string;
  measurements: number;
  minValue: number | null;
  avgValue: number | null;
  maxValue: number | null;
};

/** Kretanje parametra kroz jednu godinu — operativno čitanje, ono iz kojeg se pravi KPI. */
export type ParameterMovement = {
  parameterId: string;
  key: string;
  nameI18n: Record<string, string> | null;
  unit: string;
  year: number;
  availableYears: number[];
  measurements: number;
  minValue: number | null;
  avgValue: number | null;
  maxValue: number | null;
  days: DayPoint[];
};

/**
 * What narrows one parameter's movement.
 *
 * The range wins over the year, and that is the point of having both: asked without dates the movement
 * answers for its newest year alone, so a reader who set dates on the list would get a chart about a
 * different period than the rows under it.
 */
export type MovementFilter = {
  year?: number;
  controlPointId?: string;
  erpProductId?: string;
  from?: string;
  to?: string;
};

/**
 * One month of the analyses list: how many analyses fell in it and how many measurements they carry.
 * The month is written „2025-08", so it sorts as text and needs no second field for the year.
 */
export type MonthTally = {
  month: string;
  analyses: number;
  measurements: number;
};

export type Page<T> = {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
};

export type AnalysisFilter = {
  from?: string;
  to?: string;
  controlPointId?: string;
  kind?: ControlPointKind;
  parameterId?: string;
  /** Šifra artikla iz ERP-a. Arhiv se čita po artiklu isto koliko i po LOT-u. */
  erpProductId?: string;
  lot?: string;
  q?: string;
  page?: number;
  size?: number;
};

/** The analyses filter without paging: a tally counts the whole result, not the page being read. */
export type TallyFilter = Omit<AnalysisFilter, "page" | "size">;

function query(f: Record<string, string | number | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) {
    if (v !== undefined && v !== "" && v !== null) p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

/**
 * One article the archive holds, as the filter offers it. Count and last date are what make the list
 * usable: 586 articles sorted by name say nothing about which of them the lab actually works on.
 */
export type ArchiveArticleOption = {
  erpProductId: string;
  name: string | null;
  analyses: number;
  lastOn: string | null;
};

let pregledUSesiji: Promise<ArchiveInsights> | null = null;

/** Forgets the kept overview, so a test (or a reader after an import) starts from the server again. */
export function zaboraviPregled(): void {
  pregledUSesiji = null;
}

export const labArchiveApi = {
  articles: () => apiFetch<ArchiveArticleOption[]>(`/quality/lab-archive/articles`),
  overview: () => apiFetch<ArchiveOverview>("/quality/lab-archive/overview"),
  insights: () => {
    // Read once per browser session. The eleven aggregates behind this call take two to three seconds
    // of database time, the tab unmounts whenever another one is opened, and the archive is ten closed
    // years written by an import — so asking again on every return would buy the same numbers at the
    // same price. A failed read is not remembered, so the next attempt tries the server again. Figures
    // written by a new import appear after a page reload.
    if (!pregledUSesiji) {
      pregledUSesiji = apiFetch<ArchiveInsights>("/quality/lab-archive/insights").catch((e) => {
        pregledUSesiji = null;
        throw e;
      });
    }
    return pregledUSesiji;
  },
  analyses: (f: AnalysisFilter) =>
    apiFetch<Page<AnalysisRow>>(`/quality/lab-archive/analyses${query(f)}`),
  analysis: (id: string) => apiFetch<AnalysisDetail>(`/quality/lab-archive/analyses/${id}`),
  movement: (parameterId: string, f: MovementFilter) =>
    apiFetch<ParameterMovement>(`/quality/lab-archive/parameters/${parameterId}/movement${query(f)}`),
  // The same filter the list is read with, answered by month instead of by row. It is what the chart
  // above the list draws when no parameter is chosen: a count of analyses carries across every kind of
  // parameter, while a value cannot be put on one axis with another parameter's value.
  tally: (f: TallyFilter) => apiFetch<MonthTally[]>(`/quality/lab-archive/analyses/tally${query(f)}`),
  parameterSummary: (parameterId: string) =>
    apiFetch<ParameterSummary>(`/quality/lab-archive/parameters/${parameterId}/summary`),
  measurements: (
    parameterId: string,
    f: { from?: string; to?: string; controlPointId?: string; lot?: string; page?: number; size?: number },
  ) =>
    apiFetch<Page<MeasurementRow>>(
      `/quality/lab-archive/parameters/${parameterId}/measurements${query(f)}`,
    ),
  batchContext: (goodsLotId: string) =>
    apiFetch<BatchArchiveContext>(`/quality/lab-archive/batches/${goodsLotId}/context`),
};
